<div
  class="min-h-screen bg-gradient-to-br from-slate-900 to-green-950 flex items-center justify-center p-4"
>
  <div class="w-full max-w-sm">
    <!-- App Logo -->
    <div class="text-center mb-6">
      <div
        class="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xl shadow-green-900/50"
      >
        <lucide-icon [img]="Clock" class="text-white" [size]="28"></lucide-icon>
      </div>
      <h1 class="text-xl font-bold text-white">{{ 'CHECK_IN.TITLE' | translate }}</h1>
      <p class="text-xs text-green-300 mt-0.5">{{ 'CHECK_IN.SUBTITLE' | translate }}</p>
    </div>

    <!-- Model Loading -->
    @if (step() === 'models-loading') {
      <div class="bg-white/10 backdrop-blur rounded-2xl p-8 text-center">
        @if (modelError()) {
          <lucide-icon
            [img]="AlertCircle"
            class="text-red-400 mx-auto mb-3"
            [size]="36"
          ></lucide-icon>
          <p class="text-red-300 text-sm">{{ modelError() }}</p>
        } @else {
          <lucide-icon
            [img]="RefreshCw"
            class="text-green-300 animate-spin mx-auto mb-3"
            [size]="32"
          ></lucide-icon>
          <p class="text-white font-medium mb-3">{{ 'CHECK_IN.MODEL_LOADING' | translate }}</p>
          <div class="w-full bg-white/10 rounded-full h-2">
            <div
              class="bg-green-400 h-2 rounded-full transition-all duration-500"
              [style.width.%]="modelProgress()"
            ></div>
          </div>
          <p class="text-green-300 text-xs mt-2">{{ modelProgress() }}%</p>
        }
      </div>
    }

    <!-- Login -->
    @if (step() === 'login') {
      <div class="bg-white rounded-2xl shadow-2xl p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">
          {{ 'CHECK_IN.LOGIN_TITLE' | translate }}
        </h2>
        @if (loginError) {
          <div class="bg-red-50 text-red-600 rounded-lg p-3 text-sm mb-4">{{ loginError }}</div>
        }
        <div class="space-y-3">
          <input
            type="tel"
            [(ngModel)]="loginForm.phoneNumber"
            placeholder="+998901234567"
            class="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <input
            type="password"
            [(ngModel)]="loginForm.password"
            [placeholder]="'LOGIN.PASSWORD' | translate"
            class="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            (keyup.enter)="doLogin()"
          />
          <button
            (click)="doLogin()"
            [disabled]="loginLoading"
            class="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
          >
            <lucide-icon [img]="LogIn" [size]="18"></lucide-icon>
            {{
              loginLoading
                ? ('CHECK_IN.LOGGING_IN' | translate)
                : ('CHECK_IN.LOGIN_BTN' | translate)
            }}
          </button>
        </div>
      </div>
    }

    <!-- Ready State -->
    @if (step() === 'ready') {
      <div class="space-y-4">
        <!-- User Card -->
        <div class="bg-white/10 backdrop-blur rounded-2xl px-4 py-3 flex items-center gap-3">
          <div
            class="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-sm shrink-0"
          >
            {{ (user?.fullName ?? 'X')[0].toUpperCase() }}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-white font-medium text-sm truncate">{{ user?.fullName }}</p>
            <p class="text-green-300 text-xs">{{ user?.phoneNumber }}</p>
          </div>
          <button (click)="logout()" class="text-xs text-red-400 hover:text-red-300 shrink-0">
            {{ 'CHECK_IN.LOGOUT' | translate }}
          </button>
        </div>

        <!-- Face Not Registered Banner -->
        @if (!hasFaceRegistered()) {
          <div class="bg-amber-400/20 border border-amber-400/40 rounded-2xl p-4 text-center">
            <lucide-icon
              [img]="AlertCircle"
              class="text-amber-300 mx-auto mb-2"
              [size]="28"
            ></lucide-icon>
            <p class="text-amber-200 text-sm font-medium mb-1">
              {{ 'CHECK_IN.FACE_NOT_REG' | translate }}
            </p>
            <p class="text-amber-300/80 text-xs mb-3">
              {{ 'CHECK_IN.FACE_NOT_REG_HINT' | translate }}
            </p>
            <button
              (click)="startCamera('register')"
              class="bg-amber-400 hover:bg-amber-300 text-amber-900 font-bold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 mx-auto w-full justify-center"
            >
              <lucide-icon [img]="Camera" [size]="18"></lucide-icon>
              {{ 'CHECK_IN.REGISTER_FACE' | translate }}
            </button>
          </div>
        }

        <!-- Today Status -->
        @if (currentRecord()) {
          <div class="bg-white/10 backdrop-blur rounded-2xl p-4">
            <div class="flex items-center gap-2 mb-3">
              <div
                class="w-2 h-2 rounded-full"
                [class]="isCheckedIn ? 'bg-green-400' : 'bg-gray-400'"
              ></div>
              <span class="text-sm font-medium text-white">
                {{
                  isCheckedIn
                    ? ('CHECK_IN.TODAY.WORKING' | translate)
                    : ('CHECK_IN.TODAY.DONE' | translate)
                }}
              </span>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="bg-white/10 rounded-xl p-3 text-center">
                <p class="text-xs text-green-300 mb-1">
                  {{ 'CHECK_IN.TODAY.CHECK_IN' | translate }}
                </p>
                <p class="text-white font-bold text-lg">
                  {{ formatTime(currentRecord()!.checkInTime) }}
                </p>
              </div>
              <div class="bg-white/10 rounded-xl p-3 text-center">
                <p class="text-xs text-orange-300 mb-1">
                  {{ 'CHECK_IN.TODAY.CHECK_OUT' | translate }}
                </p>
                <p
                  class="font-bold text-lg"
                  [class]="currentRecord()!.checkOutTime ? 'text-white' : 'text-white/30'"
                >
                  {{ formatTime(currentRecord()!.checkOutTime) }}
                </p>
              </div>
            </div>
            @if (currentRecord()!.storeName) {
              <p class="text-xs text-green-300 mt-2 text-center">
                <lucide-icon [img]="MapPin" class="inline mr-1" [size]="11"></lucide-icon>
                {{ currentRecord()!.storeName }}
              </p>
            }
          </div>
        }

        <!-- Error / Success Messages -->
        @if (errorMsg()) {
          <div class="bg-red-500/20 border border-red-500/40 rounded-xl p-3 text-red-200 text-sm">
            {{ errorMsg() }}
          </div>
        }
        @if (successMsg()) {
          <div
            class="bg-green-500/20 border border-green-500/40 rounded-xl p-3 text-green-200 text-sm flex items-center gap-2"
          >
            <lucide-icon [img]="CheckCircle" [size]="16"></lucide-icon>
            {{ successMsg() }}
          </div>
        }

        <!-- Action Buttons -->
        @if (hasFaceRegistered()) {
          <div class="space-y-3">
            @if (!currentRecord()) {
              <button
                (click)="startCamera('checkin')"
                class="w-full bg-green-500 hover:bg-green-400 active:scale-95 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 text-lg shadow-lg shadow-green-900/40 transition-all"
              >
                <lucide-icon [img]="Scan" [size]="24"></lucide-icon>
                {{ 'CHECK_IN.ACTIONS.CHECK_IN' | translate }}
              </button>
            } @else if (isCheckedIn) {
              <button
                (click)="startCamera('checkout')"
                class="w-full bg-orange-500 hover:bg-orange-400 active:scale-95 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 text-lg shadow-lg shadow-orange-900/40 transition-all"
              >
                <lucide-icon [img]="Scan" [size]="24"></lucide-icon>
                {{ 'CHECK_IN.ACTIONS.CHECK_OUT' | translate }}
              </button>
            } @else {
              <div class="bg-white/10 rounded-2xl py-4 text-center text-white/60 font-medium">
                <lucide-icon
                  [img]="CheckCircle"
                  class="inline mr-2 text-green-400"
                  [size]="20"
                ></lucide-icon>
                {{ 'CHECK_IN.ACTIONS.DAY_DONE' | translate }}
              </div>
            }
            <p class="text-xs text-center text-green-400">
              {{ 'CHECK_IN.ACTIONS.SCAN_HINT' | translate }}
            </p>
          </div>
        }
      </div>
    }

    <!-- Camera / Scanning View -->
    @if (step() === 'scanning') {
      <div class="space-y-4">
        <div
          class="relative rounded-2xl overflow-hidden bg-black shadow-2xl"
          style="aspect-ratio: 4/3"
        >
          <video
            #videoEl
            autoplay
            playsinline
            muted
            class="w-full h-full object-cover"
            style="transform: scaleX(-1)"
          ></video>
          <canvas
            #canvasEl
            class="absolute inset-0 w-full h-full"
            style="pointer-events: none; transform: scaleX(-1)"
          ></canvas>

          <!-- Oval face guide -->
          <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              class="w-44 h-56 rounded-full border-2 opacity-40 transition-colors duration-300"
              [class]="
                faceStatus() === 'matched'
                  ? 'border-green-400'
                  : faceStatus() === 'no-match'
                    ? 'border-red-400'
                    : 'border-white'
              "
            ></div>
          </div>

          <!-- Status bar -->
          <div
            class="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent"
          >
            @if (faceStatus() === 'none') {
              <div class="flex items-center gap-2 text-white text-sm">
                <div class="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></div>
                {{ 'CHECK_IN.FACE.NONE' | translate }}
              </div>
            } @else if (faceStatus() === 'detected') {
              <div class="flex items-center gap-2 text-green-300 text-sm font-medium">
                <div class="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                {{ 'CHECK_IN.FACE.DETECTED' | translate }}
              </div>
            } @else if (faceStatus() === 'matched') {
              <div class="flex items-center gap-2 text-green-300 text-sm font-medium">
                <lucide-icon [img]="UserCheck" [size]="16"></lucide-icon>
                {{ 'CHECK_IN.FACE.MATCHED' | translate }}
              </div>
            } @else if (faceStatus() === 'no-match') {
              <div class="flex items-center gap-2 text-red-300 text-sm font-medium">
                <lucide-icon [img]="UserX" [size]="16"></lucide-icon>
                {{ 'CHECK_IN.FACE.NO_MATCH' | translate }}
              </div>
            }
          </div>
        </div>

        <button
          (click)="cancelScan()"
          class="w-full border border-white/20 text-white/70 hover:text-white py-3 rounded-2xl text-sm font-medium"
        >
          {{ 'CHECK_IN.CANCEL_SCAN' | translate }}
        </button>
      </div>
    }

    <!-- Processing -->
    @if (step() === 'processing') {
      <div class="bg-white/10 backdrop-blur rounded-2xl p-10 text-center">
        <lucide-icon
          [img]="RefreshCw"
          class="text-green-300 animate-spin mx-auto mb-4"
          [size]="36"
        ></lucide-icon>
        <p class="text-white font-medium">{{ 'CHECK_IN.PROCESSING' | translate }}</p>
        <p class="text-green-300 text-sm mt-1">{{ 'CHECK_IN.PROCESSING_SUB' | translate }}</p>
      </div>
    }

    <!-- Done -->
    @if (step() === 'done') {
      <div class="bg-white/10 backdrop-blur rounded-2xl p-10 text-center">
        <div
          class="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-900/50"
        >
          <lucide-icon [img]="CheckCircle" class="text-white" [size]="36"></lucide-icon>
        </div>
        <p class="text-white font-bold text-xl mb-2">{{ 'CHECK_IN.DONE' | translate }}</p>
        <p class="text-green-300 text-sm">{{ successMsg() }}</p>
        <p class="text-green-400 text-xs mt-3">{{ 'CHECK_IN.AUTO_BACK' | translate }}</p>
      </div>
    }
  </div>
</div>
